import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { CountriesTerritoriesFacade, PresentationModeFacade } from '@cpak/main-features-lib/data-store';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  filter,
  Observable,
  switchMap,
  timer,
  withLatestFrom,
} from 'rxjs';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { FeaturesEnum, RoutingUrls } from '@cpak/enums';
import { MenuConfig, NavigationLink, InAppNotification } from '@cpak/interfaces';
import { UserMenuKeys } from '@cpak/enums';
import { Menu } from 'primeng/menu';
import { TranslocoService } from '@ngneat/transloco';
import { BFFGetInboxResponsePayload, StartResponsePayload } from '@cpak/adp-cpak-bff-service-models';
import { InAppInboxModel } from '@cpak/adp-cpak-notifications-service-models';
import { UserModel } from '@cpak/adp-cpak-users-service-models';
import {
  FeatureToggleService,
  UserPermissionsService,
  InAppNotificationsFetchService,
  StartService,
} from '@cpak/main-features-lib/services';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AuthService } from '../../services/auth/auth.service';

import { NotificationsPopoverComponent } from '../notifications-popover/notifications-popover.component';
import { ButtonNamesGA } from '@cpak/consts';
import { ACCOUNT, ALLCOUNTRY, allowRoles, CATALOG, CURRENTROUT, COUNTRYARRAYLEN } from './countries-const';
import { CountriesTerritoriesService } from '@cpak/main-features-lib/services';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChatPanelComponent } from 'libs/main-features-lib/src/dashboard/components/chat-panel/chat-panel.component';

@UntilDestroy()
@Component({
  selector: 'cpak-navigation-bar',
  templateUrl: './navigation-bar.component.html',
  styleUrls: ['./navigation-bar.component.scss'],
})
export class NavigationBarComponent implements OnInit, AfterViewInit {
  private readonly accountsTitle = 'accounts';
  private countryIds: string[] = [];
  private userIds = '';
  private initUserRole: string | undefined;
  private countryLen: number | undefined;

  public readonly defaultLogoPath = '/assets/images/logo.svg';
  public readonly darkModeLogoPath = '/assets/images/logo-dark-mode.svg';
  public isAccountsActiveUrl = false;
  public isAdminActiveUrl = false;
  public menuConfig: MenuConfig<UserMenuKeys>[] = [];
  public adminLink: NavigationLink = { title: 'admin', route: `/${RoutingUrls.Maintenance}` };
  public isPresentationMode$: Observable<boolean | null>;
  public inAppNotifications$ = new BehaviorSubject<InAppNotification[]>([]);
  public countOfUnreadNotifications$ = new BehaviorSubject<number>(0);
  public isInAppNotificationsEnabled$ = this.featureToggleService.isFeatureEnabled(FeaturesEnum.InAppNotifications);
  public navigationLinks: NavigationLink[] = [
    { title: 'dashboard', route: '/dashboard' },
    { title: this.accountsTitle, route: '/accounts' },
    { title: 'catalog', route: '/catalog' },
  ];

  public readonly ButtonNamesGA = ButtonNamesGA;

  private readonly accountRouteUrlsArray: string[] = [RoutingUrls.Prototypes, RoutingUrls.Accounts, RoutingUrls.Quotes];
  private readonly adminRouteUrlsArray: string[] = [
    RoutingUrls.Maintenance,
    RoutingUrls.MasterTemplates,
    RoutingUrls.BomTemplates,
  ];
  private readonly userMenuConfig: MenuConfig<UserMenuKeys>[] = [
    {
      id: UserMenuKeys.Profile,
      label: 'openProfile',
      icon: 'alc-icon-user',
    },
  ];

  public isPresentationModeLock = false;
  @ViewChild(Menu) headerMenu!: Menu;
  @ViewChild(NotificationsPopoverComponent) notificationsPopover?: NotificationsPopoverComponent;
  private dialogRef?: DynamicDialogRef;

  constructor(
    private startService: StartService,
    private presentationModeFacade: PresentationModeFacade,
    private router: Router,
    public translocoService: TranslocoService,
    private userPermissionsService: UserPermissionsService,
    private featureToggleService: FeatureToggleService,
    private authService: AuthService,
    private inAppNotificationsFetchService: InAppNotificationsFetchService,
    private countriesTerritoriesFacade: CountriesTerritoriesFacade,
    private countriesTerritoriesService: CountriesTerritoriesService,
    private dialogService: DialogService
  ) {
    this.isPresentationMode$ = this.presentationModeFacade.getPresentationModeState();
    combineLatest([this.countriesTerritoriesFacade.getCountryAndTerritoriesState(), this.isInAppNotificationsEnabled$])
      .pipe(
        untilDestroyed(this),
        switchMap(([{ country, territories }, isInAppNotificationsEnabled]) => {
          this.startService.getStartResponse().subscribe((response: StartResponsePayload | null) => {
            if (response) {
              const { userInfo, config } = response.data;
              this.countryLen = userInfo.countryIds.length;
              const is_presentation = userInfo.user.roles.some(e => {
                return config.presentationMode.roles.includes(e);
              });
              if (config.presentationMode.countryIds.includes(country) && is_presentation) {
                this.isPresentationModeLock = true;
                this.presentationModeFacade.initPresentationMode(true);
              } else {
                this.isPresentationModeLock = false;
              }
            }
          });
          if (isInAppNotificationsEnabled) {
            return this.startPollingForInAppNotifications(country, territories || []);
          }
          return EMPTY;
        })
      )
      .subscribe(notifications => {
        if (!!notifications) this.setInAppNotifications(notifications?.inAppInbox, notifications?.users);
      });

    router.events.forEach(event => {
      if (event instanceof NavigationStart) {
        if (event.navigationTrigger === 'popstate') {
          const currentCountryCode = localStorage.getItem(this.userIds + '-selected-country-territories');
          const userCountryCodePArse = JSON.parse(currentCountryCode!) as { country: string };
          const isDefaultCountryCode =
            userCountryCodePArse.country === ALLCOUNTRY
              ? this.countryIds[0]
              : userCountryCodePArse.country
              ? userCountryCodePArse.country
              : this.countryIds[0];
          this.countriesTerritoriesService.updateCountryState(isDefaultCountryCode);
        }
      }
    });
  }

  public ngOnDestroy(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }

  public ngOnInit(): void {
    this.isAccountsActiveUrl = this.isUrlIncludesValue(this.router.url, this.accountRouteUrlsArray);
    this.isAdminActiveUrl = this.isUrlIncludesValue(this.router.url, this.adminRouteUrlsArray);

    this.router.events
      .pipe(
        filter(val => val instanceof NavigationEnd),
        untilDestroyed(this)
      )
      .subscribe(val => {
        this.isAccountsActiveUrl = this.isUrlIncludesValue((val as NavigationEnd).url, this.accountRouteUrlsArray);
        this.isAdminActiveUrl = this.isUrlIncludesValue((val as NavigationEnd).url, this.adminRouteUrlsArray);
      });

    this.userPermissionsService.hasSomeMaintenancePermission().subscribe(hasPermission => {
      if (hasPermission) {
        this.navigationLinks = [...this.navigationLinks, this.adminLink];
      }
    });

    this.initMenu();

    this.startService.getStartResponse().subscribe(startResponse => {
      if (startResponse) {
        this.countryIds = startResponse.data.userInfo.countryIds.sort();
        this.userIds = startResponse.data.userInfo.user.id;
        this.initUserRole = startResponse.data.userInfo.user.roles[0];
      }
    });

    if (
      this.countryLen &&
      this.countryLen > COUNTRYARRAYLEN &&
      this.initUserRole &&
      allowRoles.indexOf(this.initUserRole) !== -1
    ) {
      if (this.router.url.indexOf(ACCOUNT) !== -1) {
        const currentCountryCode = localStorage.getItem(this.userIds + '-selected-country-territories');

        const userCountryCodePArse = JSON.parse(currentCountryCode!) as { country: string };
        const isDefaultCountryCode =
          userCountryCodePArse.country === ALLCOUNTRY
            ? this.countryIds[0]
            : userCountryCodePArse.country
            ? userCountryCodePArse.country
            : this.countryIds[0];
        this.countriesTerritoriesService.updateCountryState(isDefaultCountryCode);
        this.countriesTerritoriesService.updateTerritoriesState({ territories: [], allTerritories: true });
      }
    }
  }

  public ngAfterViewInit(): void {
    this.markNotificationsAsReadWhenNotificationPanelOpens();
  }

  public onNavItemClick(route: string): void {
    localStorage.setItem('ifAdminRoute', route);
    if (
      this.countryLen &&
      this.countryLen > COUNTRYARRAYLEN &&
      this.initUserRole &&
      allowRoles.indexOf(this.initUserRole) !== -1
    ) {
      localStorage.setItem(CURRENTROUT, route);
      if (route === ACCOUNT || route === CATALOG) {
        const currentCountryCode = localStorage.getItem(this.userIds + '-selected-country-territories');
        const userCountryCodePArse = JSON.parse(currentCountryCode!) as { country: string };
        const isDefaultCountryCode =
          userCountryCodePArse.country === ALLCOUNTRY
            ? this.countryIds[0]
            : userCountryCodePArse.country
            ? userCountryCodePArse.country
            : this.countryIds[0];
        this.countriesTerritoriesService.updateCountryState(isDefaultCountryCode);
        this.countriesTerritoriesService.updateTerritoriesState({ territories: [], allTerritories: true });
      } else {
        if (route === '/dashboard') {
          this.countriesTerritoriesService.updateCountryState(ALLCOUNTRY);
          this.router.navigate(['/dashboard']);
        }
      }
    }
  }

  private initMenu(): void {
    this.translocoService.selectTranslate(this.userMenuConfig.map(item => item.label)).subscribe(translations => {
      this.menuConfig = this.userMenuConfig.map((item, i) => {
        item.label = translations[i];
        switch (item.id) {
          case UserMenuKeys.Profile:
            item.command = () => {
              this.router.navigate(['./profile']);
            };
            break;
        }
        return item;
      });
    });
  }

  public onPresentationModeClick(): void {
    if (this.isPresentationModeLock) {
      this.presentationModeFacade.initPresentationMode(true);
    } else {
      this.presentationModeFacade.togglePresentationMode();
    }
  }

  public onChatClick(): void {
    this.dialogRef = this.dialogService.open(ChatPanelComponent, {
      closable: false,
      styleClass: 'alc-other-user-dialog',
    });
  }

  public onPresentationModeClick_old(): void {
    this.presentationModeFacade.togglePresentationMode();
  }

  private isUrlIncludesValue(currentUrl: string, urlsArr: string[]): boolean {
    return urlsArr.some(url => currentUrl.includes(url));
  }

  isAccountsLink(navItem: NavigationLink): boolean {
    return navItem.title === this.accountsTitle;
  }

  isAdminLink(navItem: NavigationLink): boolean {
    return navItem.title === this.adminLink.title;
  }

  public toggleMenu(event: Event): void {
    this.headerMenu.toggle(event);
  }

  public openNotificationPopover(event: Event): void {
    this.notificationsPopover?.overlayPanel.toggle(event);
  }

  private markNotificationsAsReadWhenNotificationPanelOpens(): void {
    this.notificationsPopover?.overlayPanel.onShow
      .pipe(
        withLatestFrom(
          this.countriesTerritoriesFacade.getCountryAndTerritoriesState(),
          this.countOfUnreadNotifications$,
          this.isInAppNotificationsEnabled$
        ),
        untilDestroyed(this),
        switchMap(([, countryAndTerritories, countOfUnreadNotifications, isInAppNotificationsEnabled]) => {
          const { country, territories } = countryAndTerritories;
          if (isInAppNotificationsEnabled && countOfUnreadNotifications > 0) {
            return this.inAppNotificationsFetchService.markInAppNotificationsAsRead(country, territories || []);
          }
          return EMPTY;
        })
      )
      .subscribe(notifications => {
        if (!!notifications) this.setInAppNotifications(notifications?.inAppInbox, notifications?.users);
      });
  }

  private startPollingForInAppNotifications(
    countryIds: string,
    territoryIds: string[]
  ): Observable<BFFGetInboxResponsePayload['data'] | null> {
    return timer(0, 45000).pipe(
      switchMap(() => this.inAppNotificationsFetchService.loadInAppNotifications(countryIds, territoryIds)),
      catchError(() => {
        return EMPTY;
      })
    );
  }

  private setInAppNotifications(inAppInbox?: InAppInboxModel, users?: UserModel[]): void {
    if (!inAppInbox?.messages.length) {
      this.inAppNotifications$.next([]);
      this.countOfUnreadNotifications$.next(0);
      return;
    }
    const mappedNotifications = inAppInbox.messages.map(message => {
      const { id, fromUserId, subject, date, pakId, pakType, readDate } = message;
      return {
        id,
        fromUser: users?.find(user => user.id === fromUserId)?.name || '',
        subject,
        date,
        pakId,
        pakType,
        readDate,
      };
    });

    this.inAppNotifications$.next(mappedNotifications);
    this.countOfUnreadNotifications$.next(mappedNotifications.filter(notification => !notification.readDate).length);
  }
}
